import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { GuideTooltip, GuidePopup } from "@organisms/ZOnboarding/ZGuideUI";
import {
	Guide,
	GuideType,
	GuideHookProps,
	WelcomeVideoGuideHookProps,
	WelcomeVideoGuide,
	GuideHookDetail
} from "./interfaces";
import { GuideUIProps } from "@organisms/ZOnboarding/ZGuideUI/interface";
import { MediaGuideUIProps } from "../components/VideoGuide/interface";
import MediaGuideModal from "../components/VideoGuide";
import {
	useOnboardingSelector,
	useOnboardingDispatch,
	OnboardingActionCreators,
	onbordingStore
} from "../store";
import {
	Modules,
	Stages,
	Steps,
	UpdateCheckListBarDataPayload,
	UpdateCheckListBarDataRequest,
	WelcomeVideoStatus
} from "../contracts";
import { OnboardingState } from "../store/reducers";
import {
	getIncompleteStageDetail,
	getModuleDetails,
	getNextSteps,
	navigateToNextModule
} from "./useInitOnboarding";
import idleTimeout from "idle-timeout";
import IdleTimeout from "idle-timeout/dist/IdleTimeout";
import { ParentContext } from "../store/context";
import CongratulationGuideModal from "../components/CongratulationGuide";
import { CongratulationGuideUIProps } from "@/Onboarding/components/CongratulationGuide/interface";

/**
 * The hook returns welcome video guide ui props based on the props and video status & handles welcom video status
 * @param {WelcomeVideoGuideHookProps}
 * @return {MediaGuideUIProps} - returns welcome video guide ui props
 */

const useGetWelcomeVideoGuideHook = ({
	isWelcomeVideo,
	completeCurrentStep
}: WelcomeVideoGuideHookProps) => {
	const dispatch = useOnboardingDispatch();
	const completedState = useRef(false);
	const { welcomeVideoConfig } = useOnboardingSelector(
		(state: OnboardingState) => {
			return {
				welcomeVideoConfig: state.checkListBarData?.welcome_video,
				modules: state.checkListBarData?.modules ?? []
			};
		}
	);

	if (!isWelcomeVideo) {
		return null;
	}
	const handleVideoTimeUpdate = (video: HTMLVideoElement | null) => {
		if (video && !completedState.current) {
			const currentTime: number = video.currentTime;
			const duration: number = video.duration;
			const completionPercentage: number = (currentTime / duration) * 100;
			if (completionPercentage > 95) {
				completedState.current = true;
			}
		}
	};
	const guideUIProps = {
		...(WelcomeVideoGuide.guideUIProps as MediaGuideUIProps),
		videoStatus: welcomeVideoConfig?.status,
		handleVideoTimeUpdate
	};

	guideUIProps.handleNext = async newStatus => {
		if (newStatus && !guideUIProps.loading) {
			try {
				const requestPayload: UpdateCheckListBarDataPayload = {
					welcome_video: {
						...welcomeVideoConfig,
						status: newStatus
					}
				};
				const currentStatus = welcomeVideoConfig?.status;

				const request: UpdateCheckListBarDataRequest = {
					requestPayload,
					hasAPIUpdate: true
				};
				await dispatch(
					OnboardingActionCreators.updateCheckListBarData(request)
				);

				const canShowNextStep =
					![
						WelcomeVideoStatus.WATCHED,
						WelcomeVideoStatus.OPENED
					].includes(newStatus) ||
					currentStatus === WelcomeVideoStatus.WATCHED;

				if (canShowNextStep) {
					await completeCurrentStep();
				}
				guideUIProps.loading = true;
			} catch (e) {
				console.error(e);
			} finally {
				guideUIProps.loading = false;
			}
		}
	};
	return {
		...WelcomeVideoGuide,
		guideUIProps
	};
};

/**
 * The hook returns guide ui based on the given step and it's ui props & handles the guide ui events
 * @param {GuideHookProps}
 * @return { { GuideUI: ReactDOM | null }} - returns Guide UI
 */
export const useGuideHook = (props: GuideHookProps): GuideHookDetail => {
	const {
		stageId,
		stepId,
		entityId,
		handleNextButton,
		popupInputProps,
		highlightQuery
	} = props;
	const { currentGuideProgress, checkListBarData, onboardingMaps } =
		useOnboardingSelector((state: OnboardingState) => {
			return {
				currentGuideProgress: state.currentGuideProgress,
				checkListBarData: state.checkListBarData,
				onboardingMaps: state.onboardingMaps
			};
		});
	const { activeStep, activeModule, activeStage } = currentGuideProgress;
	const dispatch = useOnboardingDispatch();
	const [isGuideVisible, setIsGuideVisible] = useState<boolean>(false);
	const [idleTimer, setIdleTimer] = useState<IdleTimeout | null>(null);
	const parentContext = useContext(ParentContext);

	const handleGuideVisiblity = (visible: boolean) => {
		setIsGuideVisible(visible);
	};

	const isOnboardingEnabled =
		checkListBarData?.is_checklist_active &&
		parentContext?.featureFlag?.isOnboardingEnabled;

	const setProgressAfterWelcomeVideo = async () => {
		if (checkListBarData?.is_checklist_active) {
			const nextStep = getNextSteps();
			if (nextStep.activeModule === null) {
				const nextModuleIndex = checkListBarData.modules.findIndex(
					module => !module.is_complete
				);
				const incompleteStep =
					getIncompleteStageDetail(nextModuleIndex);
				dispatch(
					OnboardingActionCreators.updateActiveProgressData(
						incompleteStep
					)
				);
				const requestPayload: UpdateCheckListBarDataPayload = {
					active_module: nextModuleIndex
				};
				const request: UpdateCheckListBarDataRequest = {
					requestPayload,
					hasAPIUpdate: true
				};
				await dispatch(
					OnboardingActionCreators.updateCheckListBarData(request)
				);
				navigateToNextModule(incompleteStep, parentContext);
			} else {
				completeActiveStep();
			}
		} else {
			dispatch(
				OnboardingActionCreators.updateActiveProgressData({
					activeModule: null,
					activeStage: null,
					activeStep: null
				})
			);
		}
	};

	const completeActiveStep = async () => {
		if (!isOnboardingEnabled || stepId !== activeStep) {
			return;
		}
		const nextStep = getNextSteps();
		if (
			(currentStep?.stageComplete || currentStep?.moduleComplete) &&
			activeModule &&
			activeStage
		) {
			const { checkListBarData, moduleOrder, onboardingMaps } =
				onbordingStore.getState();
			const moduleIndex = moduleOrder.indexOf(activeModule);
			const stageIndex =
				onboardingMaps?.moduleStages?.[activeModule]?.indexOf(
					activeStage
				);
			if (
				checkListBarData?.modules[moduleIndex] &&
				stageIndex !== undefined
			) {
				const stageList = checkListBarData.modules[moduleIndex].stages;
				if (currentStep?.stageComplete) {
					stageList[stageIndex].is_complete = true;
				}
				if (currentStep?.moduleComplete) {
					stageList.map(stage => (stage.is_complete = true));
					checkListBarData.modules[moduleIndex].is_complete = true;
					checkListBarData.active_module = null;
				}
			} else {
				return;
			}
			const requestPayload: UpdateCheckListBarDataPayload = {
				active_module: checkListBarData.active_module,
				modules: checkListBarData.modules
			};
			if (
				checkListBarData.active_module === null &&
				checkListBarData.is_checklist_complete
			) {
				requestPayload.is_checklist_active = false;
			}
			const request: UpdateCheckListBarDataRequest = {
				requestPayload,
				hasAPIUpdate: !!currentStep.triggerAPI
			};
			await dispatch(
				OnboardingActionCreators.updateCheckListBarData(request)
			);
		}
		dispatch(OnboardingActionCreators.updateActiveProgressData(nextStep));
	};

	const resetGivenStep = (module: Modules, stage: Stages, step: Steps) => {
		if (!isOnboardingEnabled) {
			return;
		}
		const { checkListBarData, moduleOrder, onboardingMaps } =
			onbordingStore.getState() as OnboardingState;
		const moduleIndex = moduleOrder.indexOf(module);
		const stageIndex =
			onboardingMaps?.moduleStages?.[module]?.indexOf(stage);
		if (checkListBarData?.modules[moduleIndex] && stageIndex) {
			checkListBarData.modules[moduleIndex].stages[
				stageIndex
			].is_complete = false;
			dispatch(
				OnboardingActionCreators.updateActiveProgressData({
					activeModule: module,
					activeStage: stage,
					activeStep: step
				})
			);
			const requestPayload: UpdateCheckListBarDataPayload = {
				modules: checkListBarData.modules
			};
			const request: UpdateCheckListBarDataRequest = {
				requestPayload,
				hasAPIUpdate: false
			};
			dispatch(OnboardingActionCreators.updateCheckListBarData(request));
		}
	};

	let currentStep: Guide | null = useGetWelcomeVideoGuideHook({
		isWelcomeVideo: stepId === Steps.WELCOME_VIDEO,
		completeCurrentStep: setProgressAfterWelcomeVideo
	});

	const moduleDetails = activeModule && getModuleDetails(activeModule);
	const showGuide = !moduleDetails?.is_complete;

	if (!currentStep && stageId && showGuide && isOnboardingEnabled) {
		currentStep =
			onboardingMaps?.stageSteps?.[stageId]?.find(
				step => step.id == stepId
			) ?? null;
	}

	if (
		currentStep?.isEntityRequired &&
		moduleDetails?.created_entity_id &&
		entityId &&
		moduleDetails.created_entity_id !== entityId
	) {
		currentStep = null;
	}

	const showGuideWithIdleTime = () => {
		if (activeStep == stepId) {
			handleGuideVisiblity(true);
		}
		idleTimer?.pause();
	};

	useEffect(() => {
		if (idleTimer) {
			isGuideVisible
				? idleTimer.pause()
				: activeStep == stepId && idleTimer.resume();
		}
	}, [isGuideVisible]);

	useEffect(() => {
		if (activeStep == stepId) {
			const guideUIProps = currentStep?.guideUIProps as GuideUIProps;
			const timeout = guideUIProps?.idleTime;
			if (!idleTimer && timeout) {
				const newIdleTimer = idleTimeout(showGuideWithIdleTime, {
					element: document.body,
					timeout,
					loop: true
				});
				newIdleTimer.pause();
				setIdleTimer(newIdleTimer);
			}
			handleGuideVisiblity(guideUIProps?.visible ?? false);
		} else {
			idleTimer?.destroy();
			setIdleTimer(null);
			handleGuideVisiblity(false);
		}
	}, [activeStep, currentStep]);

	return useMemo((): GuideHookDetail => {
		const defaultGuideDetails = {
			GuideUI: null,
			resetGivenStep,
			completeActiveStep
		};
		if (currentStep) {
			switch (currentStep.type) {
				case GuideType.VIDEO: {
					const guideUIProps =
						currentStep.guideUIProps as MediaGuideUIProps;
					const handleClickNext =
						guideUIProps.handleNext ?? completeActiveStep;
					const GuideUI: React.FC = () => (
						<MediaGuideModal
							handleNext={handleClickNext}
							{...guideUIProps}
							visible={activeStep == stepId}
						/>
					);
					return { ...defaultGuideDetails, GuideUI };
				}
				case GuideType.POPUP: {
					const handleClose = () => {
						if (guideUIProps.closeOnClickOut) {
							guideUIProps.completeOnClickOut &&
								completeActiveStep();
							handleGuideVisiblity(false);
						}
					};
					const handleClickNext = (value?: string) => {
						if (handleNextButton) {
							handleNextButton(value);
						} else {
							completeActiveStep();
						}
					};
					const guideUIProps =
						currentStep.guideUIProps as GuideUIProps;
					const isVisible = isGuideVisible && activeStep == stepId;
					const GuideUI: React.FC = props => {
						return (
							<GuidePopup
								{...guideUIProps}
								handleClose={handleClose}
								handleNext={handleClickNext}
								visible={isVisible}
							>
								{props.children as React.ReactElement}
							</GuidePopup>
						);
					};
					return {
						...defaultGuideDetails,
						GuideUI,
						handleNext: completeActiveStep,
						handleGuideVisiblity,
						isVisible,
						stepDetail: currentStep
					};
				}
				case GuideType.NORMAL: {
					const guideUIProps =
						currentStep.guideUIProps as GuideUIProps;
					const handleClose = () => {
						if (guideUIProps.closeOnClickOut) {
							guideUIProps.completeOnClickOut &&
								completeActiveStep();
							handleGuideVisiblity(false);
						}
					};
					const handleClickNext = (value?: string) => {
						if (handleNextButton) {
							handleNextButton(value);
						} else {
							completeActiveStep();
						}
					};
					const isVisible = isGuideVisible && activeStep == stepId;
					const GuideUI: React.FC = props => {
						return (
							<GuideTooltip
								{...guideUIProps}
								handleClose={handleClose}
								handleNext={handleClickNext}
								visible={isVisible}
								popupInputProps={popupInputProps}
								highlightQuery={highlightQuery}
							>
								{props.children as React.ReactElement}
							</GuideTooltip>
						);
					};
					return {
						...defaultGuideDetails,
						GuideUI,
						handleGuideVisiblity,
						handleClose,
						isVisible,
						stepDetail: currentStep,
						handleNext: completeActiveStep
					};
				}
				case GuideType.POPUP_MODAL: {
					const guideUIProps =
						currentStep.guideUIProps as CongratulationGuideUIProps;
					const handleClickNext =
						guideUIProps.handleNext ?? completeActiveStep;
					const GuideUI: React.FC = () => (
						<CongratulationGuideModal
							handleNext={handleClickNext}
							{...guideUIProps}
							visible={activeStep == stepId}
						/>
					);
					return { ...defaultGuideDetails, GuideUI };
				}
			}
		}
		return defaultGuideDetails;
	}, [
		currentStep,
		activeStep,
		isGuideVisible,
		isOnboardingEnabled,
		popupInputProps
	]);
};
